<script setup lang="ts">
import type { GameVehicleType } from '~~/types/gamedata';

definePageMeta({ layout: "shipexkurs" });

const route = useRoute();
const router = useRouter();

const {
  data: vehicles,
  pending,
  error,
  refresh,
} = useVehicleIndex({ lazy: true });

const initialSearch = typeof route.query.q === "string" ? route.query.q : "";
const search = ref(initialSearch);

// State → URL, nicht umgekehrt: die Query dient nur als Startzustand beim
// Laden der Seite (teilbarer Link), nicht als fortlaufende Quelle der Wahrheit.
watch(search, (value) => {
  const query = { ...route.query };
  if (value) query.q = value;
  else delete query.q;
  void router.replace({ query });
});
const sizeFilters = ref<string[]>([]);
const careerFilters = ref<string[]>([]);
const manufacturerFilters = ref<string[]>([]);
const vehicleTypeFilters = ref<GameVehicleType[]>([]);
const productionStatusFilters = ref<string[]>([]);

const page = ref(1);
const itemsPerPage = ref(24);
const UNKNOWN_MANUFACTURER = '__unknown__';

function manufacturerValue(vehicle: (typeof vehicles.value)[number]): string {
  const name = vehicle.manufacturer.name?.trim();
  return !name || name.toLowerCase() === 'unknown' ? UNKNOWN_MANUFACTURER : name;
}

const filtered = computed(() => {
  const searched = searchItems(
    vehicles.value,
    ["name", "fullName", "manufacturer.name", "manufacturer.code"],
    search.value,
  );

  return searched.filter((vehicle) => {
    if (sizeFilters.value.length && !sizeFilters.value.includes(vehicle.size ?? '')) return false;
    if (careerFilters.value.length && !careerFilters.value.includes(vehicle.careerKey ?? '')) return false;
    if (manufacturerFilters.value.length && !manufacturerFilters.value.includes(manufacturerValue(vehicle))) return false;
    if (vehicleTypeFilters.value.length && !vehicleTypeFilters.value.includes(vehicle.vehicleType)) return false;
    const productionStatus = vehicle.productionStatusKey === 'in-concept' || vehicle.isConcept
      ? 'in-concept'
      : vehicle.productionStatusKey;
    if (productionStatusFilters.value.length && !productionStatusFilters.value.includes(productionStatus ?? '')) return false;
    return true;
  });
});

const paginated = computed(() => {
  const start = (page.value - 1) * itemsPerPage.value;
  return filtered.value.slice(start, start + itemsPerPage.value);
});

watch([search, sizeFilters, careerFilters, manufacturerFilters, vehicleTypeFilters, productionStatusFilters], () => {
  page.value = 1;
});

useSeoMeta({
  title: "ShipExkurs – Schiffskatalog | ArisCorp",
  description: "Durchsuche alle Schiffe im Star Citizen Universum – gefiltert nach Größe, Kategorie und Hersteller.",
  ogTitle: "ShipExkurs – Schiffskatalog",
  ogDescription: "Durchsuche alle Schiffe im Star Citizen Universum.",
});
</script>

<template>
  <div class="px-4 py-8 sm:px-6 lg:px-8">
    <div class="mb-6">
      <h1 class="!my-0 text-2xl font-bold text-white">ShipExkurs</h1>
      <p class="!my-0 text-sm text-neutral-400">
        {{ pending ? 'Fahrzeugkatalog wird geladen…' : `${filtered.length} von ${vehicles.length} Fahrzeugen` }}
      </p>
    </div>

    <ShipexkursShipFilters
      v-if="!pending && !error"
      v-model:search="search"
      v-model:sizes="sizeFilters"
      v-model:careers="careerFilters"
      v-model:manufacturers="manufacturerFilters"
      v-model:vehicle-types="vehicleTypeFilters"
      v-model:production-statuses="productionStatusFilters"
      :vehicles="vehicles"
      class="mb-6"
    />

    <div v-if="pending" aria-live="polite">
      <div class="mb-6 flex flex-wrap gap-3">
        <USkeleton class="h-10 w-full sm:w-72" />
        <USkeleton class="h-10 w-36" />
        <USkeleton class="h-10 w-36" />
      </div>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
        <div
          v-for="index in 12"
          :key="index"
          class="overflow-hidden rounded-lg bg-bunker-950 ring-1 ring-white/10"
        >
          <USkeleton class="aspect-video w-full rounded-none" />
          <div class="space-y-2 p-3">
            <USkeleton class="h-4 w-2/3" />
            <USkeleton class="h-3 w-1/2" />
            <USkeleton class="h-3 w-1/3" />
          </div>
        </div>
      </div>
    </div>

    <div v-else-if="error" class="py-16">
      <UAlert
        color="warning"
        icon="i-lucide-cloud-off"
        title="Der Fahrzeugkatalog konnte nicht geladen werden"
        description="Die Verbindung zur Schiffsdatenbank ist fehlgeschlagen."
        variant="subtle"
        :actions="[{
          label: 'Erneut versuchen',
          icon: 'i-lucide-refresh-cw',
          color: 'neutral',
          variant: 'outline',
          onClick: () => refresh(),
        }]"
      />
    </div>

    <div v-else-if="!filtered.length" class="flex flex-col items-center gap-2 py-24 text-center text-neutral-400">
      <UIcon name="i-lucide-search-x" class="size-8" />
      <p class="!my-0">Keine Fahrzeuge gefunden.</p>
    </div>

    <template v-else>
      <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3 xl:grid-cols-4">
        <ShipexkursShipCard v-for="vehicle in paginated" :key="vehicle.id" :vehicle="vehicle" />
      </div>

      <div class="mt-8 flex justify-center">
        <UPagination
          v-model:page="page"
          :total="filtered.length"
          :items-per-page="itemsPerPage"
        />
      </div>
    </template>
  </div>
</template>
